// The visitor's cookie choice, kept in a first-party cookie so the inline GTM
// bootstrap in `_OldDesign/header.tsx` can read it before React runs.
// Bumping CONSENT_VERSION makes every stored choice count as missing, so the
// banner asks everyone again.
export const CONSENT_VERSION = 1;
export const CONSENT_COOKIE = 'cdm_consent';

const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

// Cookies the old cdm.pdpa-codium.com banner set. Its choices were never
// enforced and never reached our log, so they are dropped and the visitor is
// asked again.
const LEGACY_COOKIES = [
  'cdm_session_id',
  'cdm_config_hash',
  'cdm_allowed_cookies',
  'cdm_dismissed',
];

export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
}

export interface Consent extends ConsentChoice {
  v: number;
  id: string;
  at: string;
}

const getCookie = (name: string): string | undefined => {
  const prefix = `${name}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return found ? found.substring(prefix.length) : undefined;
};

const newId = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 from random bytes, for browsers without randomUUID.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const readConsent = (): Consent | null => {
  const raw = getCookie(CONSENT_COOKIE);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (
      parsed?.v !== CONSENT_VERSION ||
      typeof parsed.id !== 'string' ||
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.marketing !== 'boolean'
    ) {
      return null;
    }
    return parsed as Consent;
  } catch (e) {
    return null;
  }
};

export const writeConsent = (
  choice: ConsentChoice,
  previous: Consent | null,
): Consent => {
  const consent: Consent = {
    v: CONSENT_VERSION,
    id: previous?.id ?? newId(),
    analytics: choice.analytics,
    marketing: choice.marketing,
    at: new Date().toISOString(),
  };
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(
    JSON.stringify(consent),
  )}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  return consent;
};

export const clearLegacyCookies = () => {
  LEGACY_COOKIES.forEach((name) => {
    if (getCookie(name) !== undefined) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
    }
  });
};

// Google Consent Mode v2 signals. Analytics drives analytics_storage; the
// three ad signals follow Marketing.
export const toGtagConsent = ({ analytics, marketing }: ConsentChoice) => {
  const ads = marketing ? 'granted' : 'denied';
  return {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
};

// Copied from the old banner's configuration on cdm.pdpa-codium.com.
export const consentText = {
  th: {
    title: 'เราใช้คุ้กกี้',
    content:
      'เราใช้คุกกี้เพื่อปรับปรุงประสบการณ์และประสิทธิภาพการใช้งานของคุณบนเว็บไซต์ของเรา คุณสามารถตั้งค่าการใช้งานคุกกี้ของคุณได้โดยคลิก เปลี่ยนการตั้งค่า ทั้งนี้ คุณสามารถศึกษาการใช้งานคุกกี้ของเราได้ที่',
    policy: 'นโยบายการใช้คุกกี้ของเรา',
    changePreference: 'เปลี่ยนการตั้งค่า',
    acceptAll: 'ยอมรับทั้งหมด',
    acceptSelected: 'ยอมรับตามตัวเลือก',
    preferenceTitle: 'Privacy Preference',
    alwaysActive: 'เปิดใช้งานตลอดเวลา',
    necessary: {
      name: 'คุกกี้ที่จำเป็น',
      description:
        'คุกกี้พื้นฐานที่จำเป็น เพื่อช่วยให้การทำงานหลักของเว็บไซต์ใช้งานได้ รวมถึงการเข้าถึงพื้นที่ที่ปลอดภัยต่าง ๆ ของเว็บไซต์ หากไม่มีคุกกี้นี้เว็บไซต์จะไม่สามารถทำงานได้อย่างเหมาะสม และจะใช้งานได้โดยการตั้งค่าเริ่มต้น โดยไม่สามารถปิดการใช้งานได้',
    },
    analytics: {
      name: 'คุกกี้สำหรับวิเคราะห์ข้อมูล',
      description:
        'คุกกี้ในส่วนวิเคราะห์ จะช่วยให้เว็บไซต์เข้าใจรูปแบบการใช้งานของผู้เข้าชมและจะช่วยปรับปรุงประสบการณ์การใช้งาน โดยการเก็บรวบรวมข้อมูลและรายงานผลการใช้งานของผู้ใช้งาน',
    },
    marketing: {
      name: 'คุกกี้เพื่อการตลาด',
      description:
        'ใช้เพื่อติดตามพฤติกรรมผู้เข้าชมเว็บไซต์เพื่อแสดงโฆษณาที่เหมาะสมสำหรับผู้ใช้งานแต่ละรายและเพื่อเพิ่มประสิทธิผลการโฆษณาสำหรับผู้เผยแพร่และผู้โฆษณาสำหรับบุคคลที่สาม',
    },
  },
  en: {
    title: 'We use cookies.',
    content:
      'We use cookies to improve your experience and performance on our website. You can manage your preferences by clicking Change Preferences.You can see how we use cookies at',
    policy: 'Cookie Policy',
    changePreference: 'Change Preferences',
    acceptAll: 'Accept All',
    acceptSelected: 'Accept Selected',
    preferenceTitle: 'Privacy Preference',
    alwaysActive: 'Always active',
    necessary: {
      name: 'Necessary',
      description:
        'Necessary cookies are required to help a website usable by enabling core functions and access to secure areas of the website. The website cannot be function properly without these cookies and they are enabled by default and cannot be disabled.',
    },
    analytics: {
      name: 'Analytics',
      description:
        'Analytics cookies help website to understand how visitors interact through the website. These cookies help to improve user experiences by collecting and reporting information.',
    },
    marketing: {
      name: 'Marketing',
      description:
        'Marketing cookies are used to track visitors across websites to display relevant advertisements for the individual user and thereby more valuable for publishers and third party advertisers.',
    },
  },
};
